import { useEffect, useState } from 'react';
import { Plus, Trash2, Save, X, GraduationCap, FileText } from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import DocumentsManager from '../components/DocumentsManager.jsx';
import { expertContextsAPI } from '../services/api.js';

// EXECUTIVE-ADMIN "Expert Mode" — Aaron authors named EXPERT CONTEXTS. On a call he
// says "be the expert on <name>"; the assistant loads that context and answers as
// the expert. Each context = a name (what he says to invoke it), optional extra
// trigger phrases, and the "context" itself (the directive/knowledge the AI adopts).
// Tenant-scoped CRUD over /expert-contexts; the panel resolves the spoken subject
// via /internal/expert-context. See docs/channel-charlie-delta.md.

const EMPTY = { name: '', triggerText: '', expertPrompt: '' };

// Trigger phrases are edited as one comma-separated line; split/join at the edges.
const toPhrases = (text) => text.split(',').map((s) => s.trim()).filter(Boolean);
const fromPhrases = (arr) => (Array.isArray(arr) ? arr.join(', ') : '');

// Module-scope so it keeps a stable identity across renders (an inline component
// would remount every keystroke and drop input focus).
function Fields({ v, on }) {
  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs uppercase tracking-wider text-stone-500 mb-1">Name — what you'll say</label>
        <input
          className="field-input"
          placeholder="e.g. the Oakwood deal"
          value={v.name}
          onChange={(e) => on({ ...v, name: e.target.value })}
        />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-wider text-stone-500 mb-1">Other ways you might say it (optional, comma-separated)</label>
        <input
          className="field-input"
          placeholder="Oakwood, the Oakwood property, the commercial deal"
          value={v.triggerText}
          onChange={(e) => on({ ...v, triggerText: e.target.value })}
        />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-wider text-stone-500 mb-1">The context — what the assistant should be an expert on</label>
        <textarea
          className="field-input min-h-[130px]"
          placeholder="You are an expert on the Oakwood commercial real-estate deal: $4.2M, 12k sq ft, closing Q3, financing through First National. Speak authoritatively about the terms, timeline, and parties…"
          value={v.expertPrompt}
          onChange={(e) => on({ ...v, expertPrompt: e.target.value })}
        />
      </div>
    </div>
  );
}

export default function ExpertContexts() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState({});
  const [creating, setCreating] = useState(EMPTY);
  const [adding, setAdding] = useState(false);
  const [docsOpen, setDocsOpen] = useState({}); // contextId -> show its documents
  const toggleDocs = (id) => setDocsOpen((s) => ({ ...s, [id]: !s[id] }));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await expertContextsAPI.list();
      setItems(r?.data || []);
    } catch (err) {
      if (err.status !== 404) setError(err.message || 'Failed to load expert contexts.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (c) =>
    setEditing((e) => ({
      ...e,
      [c.id]: { name: c.name || '', triggerText: fromPhrases(c.triggerPhrases), expertPrompt: c.expertPrompt || '' }
    }));
  const cancelEdit = (id) => setEditing((e) => { const n = { ...e }; delete n[id]; return n; });

  const saveEdit = async (id) => {
    const d = editing[id];
    if (!d?.name?.trim() || !d?.expertPrompt?.trim()) return;
    try {
      const r = await expertContextsAPI.update(id, {
        name: d.name.trim(),
        triggerPhrases: toPhrases(d.triggerText),
        expertPrompt: d.expertPrompt.trim()
      });
      setItems((list) => list.map((i) => (i.id === id ? r.data : i)));
      cancelEdit(id);
    } catch (err) {
      setError(err.body?.message || err.message || 'Save failed.');
    }
  };

  const remove = async (id, name) => {
    if (!confirm(`Delete the "${name}" expert context?`)) return;
    try {
      await expertContextsAPI.remove(id);
      setItems((list) => list.filter((i) => i.id !== id));
    } catch (err) {
      setError(err.message || 'Delete failed.');
    }
  };

  const create = async () => {
    if (!creating.name.trim() || !creating.expertPrompt.trim()) return;
    try {
      const r = await expertContextsAPI.create({
        name: creating.name.trim(),
        triggerPhrases: toPhrases(creating.triggerText),
        expertPrompt: creating.expertPrompt.trim()
      });
      setItems((list) => [r.data, ...list]);
      setCreating(EMPTY);
      setAdding(false);
      // Open the new context's documents panel so the owner can add reference
      // docs right away (docs attach to a saved context).
      setDocsOpen((s) => ({ ...s, [r.data.id]: true }));
    } catch (err) {
      setError(err.body?.message || err.message || 'Create failed.');
    }
  };

  return (
    <div className="px-6 md:px-10 py-8 max-w-3xl">
      <PageHeader
        title="Expert Mode"
        subtitle="Set up topics the assistant can be your expert on. On a call, say “be the expert on [name].”"
        actions={
          !adding && (
            <button type="button" className="btn-primary" onClick={() => setAdding(true)}>
              <Plus size={14} /> Add context
            </button>
          )
        }
      />

      {error && (
        <p className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">{error}</p>
      )}

      {adding && (
        <div className="mb-6 card border border-alfred-gold/30 space-y-3">
          <Fields v={creating} on={setCreating} />
          <p className="flex items-center gap-1.5 text-xs text-stone-500">
            <FileText size={12} /> Save the context, then add reference documents to it.
          </p>
          <div className="flex gap-2">
            <button type="button" className="btn-primary" onClick={create}>
              <Save size={14} /> Save &amp; add documents
            </button>
            <button type="button" className="btn-ghost" onClick={() => { setAdding(false); setCreating(EMPTY); }}>
              <X size={14} /> Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-stone-500">Loading…</p>
      ) : items.length === 0 ? (
        <div className="card">
          <div className="flex items-center gap-2 text-stone-300 mb-1">
            <GraduationCap size={18} className="text-alfred-gold" /> No expert contexts yet
          </div>
          <p className="text-sm text-stone-500">
            Add a topic — a name, and what the assistant should know — then on a call say
            “be the expert on that.”
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((c) => {
            const d = editing[c.id];
            if (d) {
              return (
                <li key={c.id} className="card space-y-3">
                  <Fields v={d} on={(nv) => setEditing((s) => ({ ...s, [c.id]: nv }))} />
                  <div className="flex gap-2">
                    <button type="button" className="btn-primary" onClick={() => saveEdit(c.id)}>
                      <Save size={14} /> Save
                    </button>
                    <button type="button" className="btn-ghost" onClick={() => cancelEdit(c.id)}>
                      <X size={14} /> Cancel
                    </button>
                  </div>
                </li>
              );
            }
            return (
              <li key={c.id} className="card">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <GraduationCap size={16} className="text-alfred-gold" />
                      <span className="text-stone-100 font-medium">{c.name}</span>
                    </div>
                    {Array.isArray(c.triggerPhrases) && c.triggerPhrases.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {c.triggerPhrases.map((p, i) => (
                          <span key={i} className="badge bg-stone-800 text-stone-400 border border-stone-700 text-[10px]">{p}</span>
                        ))}
                      </div>
                    )}
                    <p className="mt-2 text-sm text-stone-300 leading-relaxed whitespace-pre-wrap line-clamp-4">
                      {c.expertPrompt}
                    </p>
                  </div>
                  <div className="shrink-0 flex items-center gap-1">
                    <button
                      type="button"
                      className={`btn-ghost ${docsOpen[c.id] ? 'text-alfred-gold' : ''}`}
                      onClick={() => toggleDocs(c.id)}
                    >
                      <FileText size={14} /> Documents
                    </button>
                    <button type="button" className="btn-ghost" onClick={() => startEdit(c)}>Edit</button>
                    <button
                      type="button"
                      className="btn-ghost text-red-300 hover:text-red-200"
                      onClick={() => remove(c.id, c.name)}
                      aria-label="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {docsOpen[c.id] && (
                  <div className="mt-4 border-t border-stone-900 pt-4">
                    <DocumentsManager
                      area="expert"
                      expertContextId={c.id}
                      title="Reference documents"
                      blurb="Upload documents (or paste notes) the assistant should draw on when it's the expert on this topic. Used only when this context is invoked on a call — not on every call."
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
